import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { appAnalyticsService } from '../../services/appAnalyticsService';

interface MissedModulesHeatmapProps {
  projectId?: string;
  businessId?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

interface MissedModuleEntry {
  rep_name: string;
  module: string;
  usage_rate: number;
  used: number;
  missed: number;
  total_sessions: number;
}

const MissedModulesHeatmap: React.FC<MissedModulesHeatmapProps> = ({ projectId, businessId }) => {
  const [rawData, setRawData] = useState<MissedModuleEntry[]>([]);
  const [modules, setModules] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId && !businessId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = await appAnalyticsService.getMissedModules({ projectId: projectId || businessId });
        if (result && result.data && Array.isArray(result.data)) {
          setRawData(result.data);
          setModules(result.expected_modules || []);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch missed modules data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [projectId, businessId]);

  const getChartOption = () => {
    if (rawData.length === 0 || modules.length === 0) return {};

    // Get unique reps (top 15 by total sessions)
    const repMap = new Map<string, { name: string; totalSessions: number }>();
    rawData.forEach(d => {
      if (!repMap.has(d.rep_name)) {
        repMap.set(d.rep_name, { name: d.rep_name, totalSessions: d.total_sessions });
      }
    });
    const reps = Array.from(repMap.values())
      .sort((a, b) => b.totalSessions - a.totalSessions)
      .slice(0, 15)
      .map(r => r.name);

    const moduleLabels = modules.map(m => m.replace(/_/g, ' '));

    // Build heatmap data: [moduleIndex, repIndex, usageRate]
    const heatmapData: number[][] = [];
    let minVal = 100;
    let maxVal = 0;

    reps.forEach((rep, repIdx) => {
      modules.forEach((mod, modIdx) => {
        const entry = rawData.find(d => d.rep_name === rep && d.module === mod);
        const rate = entry ? entry.usage_rate : 0;
        heatmapData.push([modIdx, repIdx, rate]);
        if (rate < minVal) minVal = rate;
        if (rate > maxVal) maxVal = rate;
      });
    });

    return {
      tooltip: {
        position: 'top' as const,
        textStyle: { fontFamily: FONT_FAMILY, fontSize: 11 },
        formatter: (params: any) => {
          const modIdx = params.value[0];
          const repIdx = params.value[1];
          const rate = params.value[2];
          const rep = reps[repIdx];
          const mod = modules[modIdx];
          const entry = rawData.find(d => d.rep_name === rep && d.module === mod);
          return `<b>${rep}</b><br/>${mod.replace(/_/g, ' ')}<br/>Usage: ${rate}%<br/>Used: ${entry?.used || 0} / ${entry?.total_sessions || 0} sessions`;
        },
      },
      grid: { top: 10, right: 20, bottom: 80, left: 10, containLabel: true },
      xAxis: {
        type: 'category' as const,
        data: moduleLabels,
        splitArea: { show: true },
        axisLabel: { fontFamily: FONT_FAMILY, fontSize: 9, color: '#4A5568', interval: 0, rotate: 45 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'category' as const,
        data: reps,
        splitArea: { show: true },
        axisLabel: { fontFamily: FONT_FAMILY, fontSize: 9, color: '#4A5568', width: 80, overflow: 'truncate' as const },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      visualMap: {
        min: 0,
        max: 100,
        calculable: true,
        orient: 'horizontal' as const,
        left: 'center',
        bottom: 0,
        textStyle: { fontFamily: FONT_FAMILY, fontSize: 10, color: '#718096' },
        inRange: {
          color: ['#FC8181', '#FEB2B2', '#FEFCBF', '#C6F6D5', '#68D391'], // red → yellow → green
        },
        text: ['100% Used', '0% Used'],
        itemWidth: 12,
        itemHeight: 100,
      },
      series: [{
        type: 'heatmap',
        data: heatmapData,
        label: {
          show: true,
          formatter: (p: any) => `${p.value[2]}%`,
          fontFamily: FONT_FAMILY,
          fontSize: 9,
          color: '#2D3748',
        },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.3)' },
        },
      }],
    };
  };

  const chartHeight = Math.max(300, Math.min(rawData.length > 0 ? new Set(rawData.map(d => d.rep_name)).size * 30 + 120 : 300, 500));

  return (
    <ChartCard title="Missed Modules by Rep" subtitle="Module usage rate per sales rep (green = high usage, red = low/missed)" isLoading={isLoading} error={error}>
      {rawData.length > 0 ? (
        <ReactECharts option={getChartOption()} style={{ height: `${chartHeight}px`, width: '100%' }} opts={{ renderer: 'svg' }} />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            {!projectId && !businessId ? 'Select a project to view data' : 'No data available'}
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default MissedModulesHeatmap;
